import React from 'react'
import { useLocation } from '@tanstack/react-router'
import { cn } from '@/libs/utils'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'

interface UnifiedHeaderProps extends React.HTMLAttributes<HTMLElement> {
  fixed?: boolean
  showTenantSelector?: boolean
  showSearch?: boolean
  children?: React.ReactNode
  ref?: React.Ref<HTMLElement>
}

/**
 * Unified header component that consolidates:
 * - Header base functionality with scroll detection
 * - Tenant selector (conditionally shown based on route)
 * - Search functionality
 * - Theme switch and profile dropdown
 *
 * This replaces the pattern of:
 * <Header><HeaderTenantSelector /><Search />...</Header>
 */
export const UnifiedHeader = ({
  className,
  fixed = true,
  // showTenantSelector = true,
  showSearch = true,
  children,
  ...props
}: UnifiedHeaderProps) => {
  const [offset, setOffset] = React.useState(0)
  // const location = useLocation()

  React.useEffect(() => {
    const onScroll = () => {
      setOffset(document.body.scrollTop || document.documentElement.scrollTop)
    }

    // Add scroll listener to the body
    document.addEventListener('scroll', onScroll, { passive: true })

    // Clean up the event listener on unmount
    return () => document.removeEventListener('scroll', onScroll)
  }, [])

  // Auto-detect if tenant selector should be shown based on route
  // const shouldShowTenantSelector = React.useMemo(() => {
  //   if (!showTenantSelector) return false

  //   // Routes that should show tenant selector
  //   const tenantRoutes = [
  //     '/surveys',
  //     '/forms',
  //     '/articles',
  //     '/orders',
  //     '/user-management',
  //     '/contents',
  //     '/categories',
  //     '/branches',
  //     '/events',
  //   ]
  //   return tenantRoutes.some((route) => location.pathname.startsWith(route))
  // }, [location.pathname, showTenantSelector])

  return (
    <header
      className={cn(
        'mb-[16px] flex h-16 items-center gap-3 bg-background p-4 sm:gap-4',
        fixed && 'header-fixed peer/header fixed z-50 w-[inherit] rounded-md',
        offset > 10 && fixed ? 'shadow' : 'shadow-none',
        className
      )}
      {...props}
    >
      <SidebarTrigger variant='outline' className='scale-125 sm:scale-100' />
      <Separator orientation='vertical' className='h-6' />

      {/* Main content area with tenant selector and search */}
      <div className='flex w-full items-center space-x-4'>
        {/* {shouldShowTenantSelector && <HeaderTenantSelector />} */}
        {showSearch && <Search />}
        {children}
      </div>

      {/* Right side controls */}
      <div className='ml-auto flex items-center space-x-4'>
        <ThemeSwitch />
        <ProfileDropdown />
      </div>
    </header>
  )
}

UnifiedHeader.displayName = 'UnifiedHeader'

/**
 * Hook to determine if current route should show tenant selector
 * Can be used by components that need to conditionally show tenant-related content
 */
export const useShowTenantSelector = () => {
  const location = useLocation()

  return React.useMemo(() => {
    const tenantRoutes = [
      '/surveys',
      '/forms',
      '/articles',
      '/orders',
      '/user-management',
      '/contents',
      '/categories',
    ]
    return tenantRoutes.some((route) => location.pathname.startsWith(route))
  }, [location.pathname])
}

/**
 * Configuration object for routes that support tenant selection
 * This centralizes the route configuration for easier maintenance
 */
export const TENANT_SUPPORTED_ROUTES = [
  '/surveys',
  '/forms',
  '/articles',
  '/orders',
  '/user-management',
  '/contents',
  '/categories',
] as const

export type TenantSupportedRoute = (typeof TENANT_SUPPORTED_ROUTES)[number]
